import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { CHAT_WIDGET_FAQS } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";
import { comMarketing, KW3_CHAT_WIDGET } from "../data/seo/kw3OnPage";

const keyFeatures = [
    {
        title: "One-Line Install",
        description: "Drop a single <script> tag onto any site or use the React/Next.js NPM package. Live in under five minutes — no rebuild required.",
    },
    {
        title: "Fully Brandable",
        description: "Theme colors, gradients, launcher styles, avatar, welcome prompts, and conversation tone — every pixel matches your brand.",
    },
    {
        title: "Streaming Responses",
        description: "Tokens stream the moment they're generated, so users see typing within 200ms instead of waiting for a full reply.",
    },
    {
        title: "Knowledge-Grounded Answers",
        description: "Every reply is backed by your knowledge base, with inline citations linking back to the source page or document.",
    },
    {
        title: "File Uploads & Rich Cards",
        description: "Customers can drop screenshots, PDFs, or invoices into the chat. The agent reads them and responds with action cards or buttons.",
    },
    {
        title: "Live Human Handover",
        description: "Escalation rules transfer the conversation to a human agent — with the full transcript, context, and customer history attached.",
    },
    {
        title: "Omnichannel",
        description: "Same widget, same brain — embedded on web, WhatsApp, Slack, Instagram, Facebook Messenger and email inbox.",
    },
    {
        title: "50+ Languages",
        description: "Auto-detects language per visitor and replies natively. From Spanish to Hindi to Japanese, no extra config required.",
    },
    {
        title: "Privacy & Compliance",
        description: "Region-locked data residency, opt-in transcript PII redaction (email, phone, credit card, SSN, IP, address), audit logs, and SOC 2-aligned controls. GDPR / DPDP friendly out of the box.",
    },
];

const installSteps = [
    {
        step: "1",
        title: "Pick a chat widget template",
        description: "Start from a vertical-tuned template — Support, Sales, Booking, Lead Gen — or design from scratch.",
    },
    {
        step: "2",
        title: "Connect your knowledge base",
        description: "Crawl your website or upload PDFs, docs, and FAQs. The agent grounds every answer in your content.",
    },
    {
        step: "3",
        title: "Brand it",
        description: "Match colors, copy, position, and behavior to your brand. Preview live before publishing.",
    },
    {
        step: "4",
        title: "Embed and ship",
        description: "Paste the script tag, install the npm package, or activate the WordPress plugin. Done.",
    },
];

const primaryKeywords = [
    "AI chat widget",
    "AI chatbot for website",
    "Embeddable AI chat",
    "Knowledge base chatbot",
    "Live chat AI",
    "Customer support chatbot",
    "GPT chat widget",
    "AI chatbot SaaS",
];

interface ChatTurn {
    role: "User" | "AI";
    text: string;
}

const chatScript: ChatTurn[] = [
    { role: "User", text: "Do you support multi-language?" },
    { role: "AI", text: "Yes! We auto-detect 50+ languages and reply natively in each one." },
    { role: "User", text: "How do I embed this widget on my React app?" },
    { role: "AI", text: "Install @flogpt/widget-react and drop <FloGPTWidget widgetId=\"…\" /> in your layout." },
];

export default function ChatWidgetMarketingPage() {
    const kw3 = comMarketing();
    usePageSeo({
        title: kw3 ? KW3_CHAT_WIDGET.title : "AI Chat Widget — Embed an AI Agent on Any Website | FloGPT",
        description: kw3
            ? KW3_CHAT_WIDGET.description
            : "Embed a beautiful, brandable AI chat widget on any website in under five minutes. Streams answers grounded in your knowledge base, supports 50+ languages, and escalates to a human when needed.",
        keywords: primaryKeywords.join(", "),
        canonical: "/ai-chat-widget",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT AI Chat Widget",
                description:
                    "Embeddable AI chat widget for any website. Knowledge-grounded answers in 50+ languages with sub-200ms streaming, file uploads, rich cards, and live human handover.",
                path: "/ai-chat-widget",
                serviceType: "AI Chat Widget",
                features: [
                    "One-line install (script tag, NPM, or WordPress plugin)",
                    "50+ language auto-detection",
                    "Knowledge-grounded answers with citations",
                    "Streaming responses (<200ms)",
                    "File uploads and rich cards",
                    "Live human handover with full context",
                    "Omnichannel: web, WhatsApp, Slack, Instagram, Messenger, email",
                    "GDPR / DPDP / SOC 2-aligned",
                ],
            }),
        [],
    );

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-orange-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                        <div className="absolute left-0 bottom-0 h-96 w-96 rounded-full bg-teal-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-6">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                    Chat Widget · Web · WhatsApp · Slack
                                </div>
                                <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                    {kw3 ? KW3_CHAT_WIDGET.h1Lead : "An AI Chat Widget Your Customers Actually"}{" "}
                                    <motion.span
                                        className="inline-block"
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        style={{
                                            background: "linear-gradient(90deg, #f97316, #ec4899, #3b82f6, #f97316)",
                                            backgroundSize: "200% 100%",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Use
                                    </motion.span>
                                </h1>
                                <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
                                    {kw3
                                        ? `${KW3_CHAT_WIDGET.intro} ${KW3_CHAT_WIDGET.follow}`
                                        : "Beautiful, brandable, and fast. Embed a knowledge-grounded AI chat widget on any website in under five minutes — and let it answer in 50+ languages while you sleep."}
                                </p>
                                {kw3 ? (
                                    <p className="mt-4 max-w-xl text-base text-slate-400 leading-relaxed">
                                        Agencies use the same embed as an{" "}
                                        <Link to="/use-cases/insurance" className="font-medium text-orange-300 hover:text-orange-200">
                                            insurance chat platform
                                        </Link>
                                        .
                                    </p>
                                ) : null}
                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                    >
                                        Start Building Free
                                    </Link>
                                    <a
                                        href="#book-free-demo"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Book a Demo
                                    </a>
                                </div>
                                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span>· One-line install</span>
                                    <span>· 50+ languages</span>
                                    <span>· Stream-first UX</span>
                                </div>
                            </motion.div>

                            {/* Animated chat preview */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-teal-500/20 to-emerald-500/20 blur-2xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl p-6 sm:p-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden bg-slate-800">
                                            <img
                                                src="/images/flogpt-iso.png"
                                                alt="FloGPT"
                                                width={48}
                                                height={48}
                                                decoding="async"
                                                className="h-full w-full object-contain"
                                            />
                                        </div>
                                        <div>
                                            <div className="text-white font-semibold">FloGPT Widget</div>
                                            <div className="text-xs text-emerald-300">Online · &lt; 200 ms</div>
                                        </div>
                                    </div>
                                    <div className="mt-5 space-y-3 min-h-[260px]">
                                        {chatScript.map((m, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ opacity: 0, y: 8 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.1 * i }}
                                                viewport={{ once: true }}
                                                className={`w-fit max-w-[85%] rounded-2xl px-4 py-2 text-sm shadow ${
                                                    m.role === "AI"
                                                        ? "bg-slate-800/80 text-slate-100"
                                                        : "bg-gradient-to-r from-orange-500 to-pink-500 text-white ml-auto"
                                                }`}
                                            >
                                                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{m.role}</div>
                                                {m.text}
                                            </motion.div>
                                        ))}
                                    </div>
                                    <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4 text-xs text-slate-400">
                                        <span>Grounded · 3 citations</span>
                                        <span>Stream · 184 ms</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* WHY DIFFERENT */}
                <section className="mx-auto max-w-4xl px-6 lg:px-8 py-14 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-6"
                    >
                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Stop ignoring 70% of your visitors.
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Most websites turn high-intent traffic into bounce. Live chat is great until your team is offline, FAQs go stale, and contact forms are graveyards.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">
                                The FloGPT chat widget answers instantly, in the visitor's own language, grounded in your knowledge base — and hands off to a human only when it actually matters.
                            </p>
                        </article>

                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-orange-300 via-pink-300 to-blue-300 bg-clip-text text-transparent">
                                    Why this isn't another scripted chatbot
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Decision-tree chatbots make customers click "talk to a human" by message #2.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">FloGPT's chat widget:</p>
                            <ul className="mt-4 space-y-3 text-slate-200">
                                {[
                                    "Understands free-form questions, not menu options",
                                    "Reads your knowledge base, docs, and PDFs in real time",
                                    "Calls real tools mid-conversation — Shopify, calendars, your CRM",
                                    "Hands off to a human with full context when escalation is needed",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-orange-400 via-pink-400 to-blue-400 shadow-[0_0_0_3px_rgba(251,146,60,0.15)]" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-slate-100 font-medium">It reads, thinks, and replies like your best support rep.</p>
                        </article>
                    </motion.div>
                </section>

                {/* INSTALL DEMO */}
                <section className="relative overflow-hidden">
                    <div className="absolute inset-0 -z-10 opacity-30">
                        <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-teal-500/30 blur-3xl" />
                        <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true, amount: 0.3 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500" />
                                    One-Line Install
                                </div>
                                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                                    Embed in five minutes. Configure forever.
                                </h2>
                                <p className="mt-4 text-slate-300 leading-relaxed">
                                    A single script tag is all it takes — no rebuild, no DNS change, no tracking pixel. The widget loads async and never blocks your page.
                                </p>
                                <ul className="mt-8 grid grid-cols-2 gap-4 text-slate-300">
                                    {[
                                        "Script tag · 1 line",
                                        "React / Next.js NPM",
                                        "WordPress plugin",
                                        "Lazy-loaded · 0 perf hit",
                                    ].map((t) => (
                                        <li key={t} className="flex items-center gap-2">
                                            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true, amount: 0.3 }}
                            >
                                <div className="relative">
                                    <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-teal-500/20 to-emerald-500/20 blur-xl" />
                                    <div className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                                        <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300 font-mono">
                                            {[
                                                "<script src=\"https://cdn.flogpt.com/widget.js\"></script>",
                                                "<script>",
                                                "  FloGPT.init({",
                                                "    widgetId: 'YOUR_ID',",
                                                "    primaryColor: '#f97316',",
                                                "    launcherPosition: 'bottom-right',",
                                                "    welcomeMessage: 'Hi! Ask me anything.'",
                                                "  })",
                                                "</script>",
                                            ].map((line, i) => (
                                                <div key={i} className="flex items-start gap-3">
                                                    <span className="select-none text-slate-600 w-6 text-right">{i + 1}</span>
                                                    <span className="whitespace-pre-wrap">{line}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-6 grid grid-cols-2 gap-4 text-xs">
                                            {installSteps.map((s) => (
                                                <div key={s.step} className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
                                                    <div className="text-orange-400 font-semibold mb-1">Step {s.step}</div>
                                                    <div className="font-semibold text-white">{s.title}</div>
                                                    <p className="mt-1 text-slate-400 leading-relaxed">{s.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* KEY FEATURES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Everything the FloGPT chat widget can do</h2>
                        <p className="mt-4 text-slate-300">
                            One widget. One brain. Every channel your customers live on.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {keyFeatures.map((feature, index) => (
                            <article
                                key={feature.title}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-orange-400/40"
                            >
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-500 to-pink-500" />
                                <div className="flex items-center justify-between">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/15 border border-orange-400/30">
                                        <svg className="h-5 w-5 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3 className="mt-5 text-lg font-semibold text-white">{feature.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {/* RELATED LINKS */}
                <section className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7">
                        <h2 className="text-lg font-semibold text-white">Pair the chat widget with</h2>
                        <p className="mt-2 text-slate-400 text-sm">The chat widget is even better when it's grounded in your content and routes through a smart workflow.</p>
                        <div className="mt-5 grid gap-4 md:grid-cols-2">
                            <Link to="/ai-knowledge-base" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-orange-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Knowledge Base →</div>
                                <p className="mt-1 text-sm text-slate-400">Crawl your site, docs, and PDFs into searchable AI memory.</p>
                            </Link>
                            <Link to="/ai-workflow-builder" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-orange-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Workflow Builder →</div>
                                <p className="mt-1 text-sm text-slate-400">Design conversation flows visually with branching and tool calls.</p>
                            </Link>
                        </div>
                    </div>
                </section>

                <FAQSection
                    id="chat-widget"
                    eyebrow="Chat Widget · FAQ"
                    heading="Common questions about the AI chat widget"
                    items={CHAT_WIDGET_FAQS}
                />

                {/* CTA + BOOKING */}
                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">See your own chat widget in action</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free 20-minute consultation. We'll wire up a live demo trained on your own content.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Book a Free Chat Widget Demo"
                            subtitle="Tell us about your site and we'll have a working agent ready before the call"
                            submitLabel="Book Free Demo"
                        />
                    </motion.div>
                </section>
            </main>
            <Footer />

            <JsonLd id="service-chat-widget" data={serviceSchema} />
        </div>
    );
}
